import {type ChangeEvent, useCallback, useEffect, useRef} from 'react';

import {type SearchPanel} from '@devexpress/dx-react-grid-material-ui';
import Search from '@mui/icons-material/Search';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';

import {OptixTableToolbarSearchMore} from './OptixTableToolbarSearchMore';
import {useOptixTableContext} from '../../OptixTableContext';
import {Field} from '../Field';

export default function OptixTableToolbarSearch({
  value,
  onValueChange,
}: SearchPanel.InputProps): JSX.Element {
  const {searchText, autoFocusSearch, filterOptions, filters, onFiltersChange} =
    useOptixTableContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const handleValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onValueChange(event.target.value);
    },
    [onValueChange],
  );

  useEffect(() => {
    if (!autoFocusSearch) return;
    // setTimeout(0) defers the focus past the MUI Dialog focus trap, which
    // otherwise reclaims focus to the dialog paper right after this mounts.
    const timer = window.setTimeout((): void => {
      inputRef.current?.focus();
    }, 0);
    return (): void => {
      window.clearTimeout(timer);
    };
  }, [autoFocusSearch]);

  return (
    <>
      {filterOptions && filterOptions.length > 0 && (
        <OptixTableToolbarSearchMore />
      )}
      {filters?.map((filter) => (
        <Chip
          color="secondary"
          key={filter.name}
          label={filter.label}
          onDelete={() => {
            if (onFiltersChange) {
              onFiltersChange(filters.filter(({name}) => name !== filter.name));
            }
          }}
        />
      ))}
      <Field
        inputRef={inputRef}
        placeholder={searchText ?? 'Search'}
        endAdornment={
          <InputAdornment position="end">
            <Search color="action" />
          </InputAdornment>
        }
        value={value}
        onChange={handleValueChange}
      />
    </>
  );
}
